<template>
  <div v-if="Object.keys(groupedFaqs).length === 0">
    <div class="mx-6 my-6 h3">
      {{ $t('faq_no_faq_available') }}
    </div>
  </div>
  <div
    v-else
    class="md:mt-5"
  >
    <div
      v-for="(subcategories, category) in groupedFaqs"
      :key="category"
      class="mx-2 my-6"
    >
      <div
        class="h3 category mb-6"
        :data-i18n="$t('faq_category_label')"
      >
        {{ category !== $t('faq_no_category') ? category : $t('faq_no_category') }}
      </div>
      <div
        v-for="subcategory in Object.values(subcategories)"
        :key="subcategory.key"
        class="pb-6"
      >
        <div
          class="h5 subcategory w-full flex bg-white-blue px-4 py-4"
          @click="toggleFaq(subcategory.key)"
        >
          {{ Object.keys(subcategories).find(key => subcategories[key] === subcategory) }}
          <fa
            icon="chevron-down"
            :class="{ 'rotate-icon': isFaqVisible[subcategory.key] }"
          />
        </div>
        <client-only>
          <TransitionGroup
            class="questions"
            name="grow-fade"
            tag="div"
          >
            <div
              v-if="isFaqVisible[subcategory.key]"
              class="faq-wrapper"
            >
              <div
                v-for="(faq, index) in subcategory.faqs"
                :key="index"
                class="bubble flex flex-col bg-lightest-gray rounded-2xl pl-4 md:pl-5 pr-4 py-4 mb-4 ml-6"
              >
                <div class="flex flex-row question pb-2 md:pb-0">
                  <p
                    class="question-label font-bold py-0 my-0"
                    :data-i18n="$t('faq_question_label')"
                  >
                    {{ $t('faq_question_label') }}
                  </p>
                  <span class="font-bold">
                    {{ faq.question }}
                  </span>
                </div>
                <div class="flex flex-row answer">
                  <p
                    class="py-0 my-0"
                    :data-i18n="$t('faq_answer_label')"
                  >
                    {{ $t('faq_answer_label') }}
                  </p>
                  <span>
                    {{ faq.answer }}
                  </span>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </client-only>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()

const props = defineProps({
  faqs: {
    type: Object,
    default: () => {},
  },
})

const isFaqVisible = ref({})

const groupedFaqs = computed(() => {
  if (!props.faqs) {
    return {}
  }
  let grouped = props.faqs.reduce((grouped, faq, index) => {
    const category = faq.category || t('faq_no_category')
    const subcategory = faq.subcategory || t('faq_no_category')
    const key = `${category}-${subcategory}-${index}` // unique key
    if (!grouped[category]) {
      grouped[category] = {}
    }
    if (!grouped[category][subcategory]) {
      grouped[category][subcategory] = { faqs: [], key }
    }
    grouped[category][subcategory].faqs.push(faq)
    return grouped
  }, {})

  // Sort the categories so that 'Övrigt' is last
  grouped = Object.keys(grouped).sort((a, b) => {
    if (a === 'Övrigt') {
      return 1
    }
    if (b === 'Övrigt') {
      return -1
    }
    return a.localeCompare(b)
  }).reduce((obj, key) => {
    obj[key] = grouped[key]
    return obj
  }, {})

  // Make the first subcategory visible by default
  const firstCategory = Object.keys(grouped)[0]
  if (firstCategory) {
    const firstSubcategory = Object.values(grouped[firstCategory])[0]
    if (firstSubcategory) {
      isFaqVisible.value[firstSubcategory.key] = true
    }
  }

  return grouped
})

const toggleFaq = (key) => {
  isFaqVisible.value[key] = !isFaqVisible.value[key]
}
</script>

<style lang="scss" scoped>
.category {
  font-family: theme('fontFamily.serif');
}

.subcategory {
  cursor: pointer;
  justify-content: space-between;
}

.question-label {
  color: theme('colors.medium-blue');
}

.bubble {
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: calc(50% - 15px);
    left: 0;
    width: 0;
    height: 0;
    border-left: 25px solid transparent;
    border-right: 25px solid transparent;
    border-bottom: 30px solid #f5f5f5;
    margin-left: -28px;
    transform: translateY(-50%);
    transform: rotate(-90deg);
    z-index: -1;
  }
}

.rotate-icon {
  transition: transform 0.2s;
  transform: rotate(180deg);
}

.question{
  gap: 10px
}
.answer{
  gap: 18px
}

.grow-fade-enter-active,
.grow-fade-leave-active {
  transition: max-height .3s ease, opacity .3s ease-in-out;
  overflow: hidden;
}

.grow-fade-enter-from,
.grow-fade-leave-to {
  max-height: 0;
  opacity: 0;
}

.grow-fade-enter-to,
.grow-fade-leave-from {
  max-height: 100vh;
  opacity: 1;
}
</style>